import { useState, useEffect, useCallback, useRef } from 'react'
import { apiClient } from '../services/api'

export function useApi<T>(apiCall: () => Promise<T>, dependencies: unknown[] = []) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const apiCallRef = useRef(apiCall)

  // Update the ref when apiCall changes
  useEffect(() => {
    apiCallRef.current = apiCall
  }, [apiCall])

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await apiCallRef.current()
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
    } finally {
      setLoading(false)
    }
  }, dependencies)

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return { data, loading, error, refetch: fetchData }
}

export function useStats() {
  return useApi(() => apiClient.getStats(), [])
}

export function useDeviceStatus(params: Parameters<typeof apiClient.getDeviceStatus>[0] = {}) {
  return useApi(() => apiClient.getDeviceStatus(params), [JSON.stringify(params)])
}

export function useLogs(params: Parameters<typeof apiClient.getLogs>[0] = {}) {
  return useApi(() => apiClient.getLogs(params), [JSON.stringify(params)])
}

export function useErrors(params: Parameters<typeof apiClient.getErrors>[0] = {}) {
  return useApi(() => apiClient.getErrors(params), [JSON.stringify(params)])
}

export function useEndpoints() {
  return useApi(() => apiClient.getEndpoints(), [])
}

export function useHealth() {
  return useApi(() => apiClient.getHealth(), [])
}
